/** Pure logic extracted from `CanvasTopBar.tsx`'s "项目操作" menu
 * (VistoeraTASK-00250 §二–§二十六) so double-submit guards and menu
 * shape can be unit-tested without rendering React/antd.
 *
 * §三 — the menu contains EXACTLY these three actions, in this exact
 * order (New → Duplicate → divider → Delete). Any test/consumer that
 * wants to assert "menu shows exactly N/D/Del" should compare against
 * this single source of truth rather than hardcoding the list twice. */
export const PROJECT_ACTION_MENU_ITEM_KEYS = ['create', 'duplicate', 'delete'] as const

export type ProjectActionMenuKey = (typeof PROJECT_ACTION_MENU_ITEM_KEYS)[number]

/** §六/§九/§十三 — each action gets its own independent double-submit
 * guard (state/ref set to true for the duration of its own async call);
 * these three tiny predicates are the guard logic itself, kept
 * side-effect-free so both the guard-holds-open-until-resolved
 * invariant and the "not currently busy" happy path are directly
 * assertable in isolation. */
export function canStartCreateProject(creating: boolean): boolean {
  return !creating
}

export function canStartDuplicateProject(duplicating: boolean): boolean {
  return !duplicating
}

/** §十一/§十三 CASE K — guards the `Modal.confirm` OK handler itself
 * (ref-based, so it is checked synchronously even within the same
 * tick a rapid double-click on the modal's OK button would produce),
 * independent of/in addition to `Modal.confirm`'s own built-in
 * pending-disable-OK-button behavior. */
export function canConfirmDeleteProject(deleteInFlight: boolean): boolean {
  return !deleteInFlight
}

/** §二 — while ANY of the three actions is in flight, the trigger
 * button itself shows a busy/disabled state (replacing the "···" icon
 * with a spinner), matching the previous single-button's existing
 * busy-state contract 1:1. */
export function resolveProjectActionMenuTriggerBusy(creating: boolean, duplicating: boolean, deleting: boolean): boolean {
  return creating || duplicating || deleting
}

/** Narrows an antd `Menu.onClick` event's raw `key` (untyped string)
 * down to one of the three known actions, or `null` for anything else
 * (defensive — antd menu keys are always strings at the type level). */
export function resolveProjectActionMenuClickTarget(key: string): ProjectActionMenuKey | null {
  return (PROJECT_ACTION_MENU_ITEM_KEYS as readonly string[]).includes(key) ? (key as ProjectActionMenuKey) : null
}
